import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { timeApi } from "../api"
import { useAuth } from "./AuthContext"
import { usePermissions } from "../permissions"

const TimerContext = createContext(null)

/** Past this, the bar asks whether the timer was left running. */
const LONG_RUNNING_SECONDS = 8 * 60 * 60

/** How often to re-read the server's view. Rare, because the tick is local. */
const RECONCILE_MS = 60 * 1000

/** Tabs tell each other about a start or stop through this. */
const CHANNEL_NAME = "tallywise.timer"

/**
 * The one running timer, wherever it was started.
 *
 * ── Ticking locally, agreeing with the server ───────────────────────────────
 *
 * A request per second would be a request per second per person all day. So the
 * elapsed seconds are counted in the browser, and the SERVER'S clock is the
 * origin: `GET /time/running` returns both the entry and the server's time, and
 * the difference between that and the browser's is held as an offset. A laptop
 * three minutes fast then still shows the truth, and the number two colleagues
 * see for the same timer is the same number.
 *
 * ── Why tabs talk to each other ─────────────────────────────────────────────
 *
 * Stopping a timer in one tab leaves every other tab counting up forever. A
 * BroadcastChannel makes the stop immediate everywhere; the reconcile below is
 * the backstop for a device that was not listening — a phone, or a tab that was
 * asleep.
 *
 * ── Why it reconciles on focus ──────────────────────────────────────────────
 *
 * A background tab's timers are throttled to once a minute or stopped
 * altogether, so coming back to a tab that has been hidden for an hour would
 * show an hour-old number. Reading the server on focus is what makes returning
 * to a tab feel like it was never away.
 */
export function TimerProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const { can } = usePermissions()
  const mayTrack = can("time_tracking", "log")

  const [running, setRunning] = useState(null)
  const [elapsed, setElapsed] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [justStopped, setJustStopped] = useState(null)

  /** serverNow - browserNow, in ms. Added to Date.now() to get the server's clock. */
  const clockOffset = useRef(0)
  const channel = useRef(null)

  const applyRunning = useCallback((payload) => {
    if (payload?.server_time) {
      clockOffset.current = new Date(payload.server_time).getTime() - Date.now()
    }
    setRunning(payload?.running ?? null)
    setElapsed(payload?.running?.elapsed_seconds ?? 0)
  }, [])

  const reconcile = useCallback(async () => {
    if (!isAuthenticated || !mayTrack) return
    try {
      applyRunning(await timeApi.running())
    } catch {
      // A failed poll is not a stopped timer. Leaving the last known state up
      // beats blanking the bar every time the network hiccups.
    }
  }, [isAuthenticated, mayTrack, applyRunning])

  // First read, and a slow reconcile afterwards.
  useEffect(() => {
    if (!isAuthenticated || !mayTrack) {
      setRunning(null)
      setElapsed(0)
      return undefined
    }
    reconcile()
    const id = setInterval(reconcile, RECONCILE_MS)
    return () => clearInterval(id)
  }, [isAuthenticated, mayTrack, reconcile])

  /*
   * The local tick. One interval for the whole app, not one per component.
   *
   * A paused timer has no tick at all rather than a tick that computes the same
   * number every second: the server already sent the frozen elapsed, and the
   * arithmetic that keeps it frozen lives there, where the pause times are.
   * Re-deriving it here would be a second implementation of the same rule, and
   * the two would disagree the first time one of them changed.
   */
  useEffect(() => {
    if (!running || running.paused) return undefined
    const id = setInterval(() => {
      const serverNow = Date.now() + clockOffset.current
      const started = new Date(running.started_at).getTime()
      const paused = Number(running.paused_seconds) || 0
      setElapsed(Math.max(0, Math.round((serverNow - started) / 1000) - paused))
    }, 1000)
    return () => clearInterval(id)
  }, [running])

  // Back from a background tab, or back online: ask rather than assume.
  useEffect(() => {
    if (!isAuthenticated || !mayTrack) return undefined
    const onWake = () => {
      if (document.visibilityState === "visible") reconcile()
    }
    document.addEventListener("visibilitychange", onWake)
    window.addEventListener("focus", reconcile)
    window.addEventListener("online", reconcile)
    return () => {
      document.removeEventListener("visibilitychange", onWake)
      window.removeEventListener("focus", reconcile)
      window.removeEventListener("online", reconcile)
    }
  }, [isAuthenticated, mayTrack, reconcile])

  // Other tabs.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined
    channel.current = new BroadcastChannel(CHANNEL_NAME)
    channel.current.onmessage = (event) => {
      if (event.data?.type === "changed") reconcile()
    }
    return () => {
      channel.current?.close()
      channel.current = null
    }
  }, [reconcile])

  const announce = useCallback(() => {
    try {
      channel.current?.postMessage({ type: "changed", at: Date.now() })
    } catch {
      // A closed channel during unmount. The reconcile interval still covers it.
    }
  }, [])

  const start = useCallback(
    async (payload) => {
      setBusy(true)
      setError(null)
      try {
        const data = await timeApi.start(payload)
        applyRunning(data)
        // Reported so the UI can say "your other timer was stopped" rather than
        // leaving somebody to notice it went.
        setJustStopped(data.stopped ?? null)
        announce()
        return data
      } catch (err) {
        setError(err.userMessage || "Could not start the timer")
        throw err
      } finally {
        setBusy(false)
      }
    },
    [applyRunning, announce],
  )

  const stop = useCallback(
    async (description) => {
      setBusy(true)
      setError(null)
      try {
        const data = await timeApi.stop(description ? { description } : {})
        setRunning(null)
        setElapsed(0)
        setJustStopped(data.stopped ?? null)
        announce()
        return data
      } catch (err) {
        setError(err.userMessage || "Could not stop the timer")
        throw err
      } finally {
        setBusy(false)
      }
    },
    [announce],
  )

  /*
   * Pause and resume.
   *
   * Both replace the whole running state from the response rather than toggling
   * a local flag, because the response also carries `paused_seconds` — and the
   * tick above subtracts it. Optimistically flipping `paused` without it would
   * show the right icon and the wrong clock.
   */
  const setPaused = useCallback(
    async (paused) => {
      setBusy(true)
      setError(null)
      try {
        const data = paused ? await timeApi.pause() : await timeApi.resume()
        applyRunning(data)
        announce()
        return data
      } catch (err) {
        setError(err.userMessage || (paused ? "Could not pause the timer" : "Could not resume the timer"))
        throw err
      } finally {
        setBusy(false)
      }
    },
    [applyRunning, announce],
  )

  const pause = useCallback(() => setPaused(true), [setPaused])
  const resume = useCallback(() => setPaused(false), [setPaused])

  const value = useMemo(
    () => ({
      running,
      elapsed,
      busy,
      error,
      justStopped,
      mayTrack,
      paused: Boolean(running?.paused),
      longRunning: elapsed > LONG_RUNNING_SECONDS,
      start,
      stop,
      pause,
      resume,
      reconcile,
      dismissStopped: () => setJustStopped(null),
      clearError: () => setError(null),
    }),
    [running, elapsed, busy, error, justStopped, mayTrack, start, stop, pause, resume, reconcile],
  )

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>
}

export function useTimer() {
  return (
    useContext(TimerContext) ?? {
      running: null,
      elapsed: 0,
      busy: false,
      mayTrack: false,
      paused: false,
      longRunning: false,
      start: async () => {},
      stop: async () => {},
      pause: async () => {},
      resume: async () => {},
      reconcile: async () => {},
      dismissStopped: () => {},
      clearError: () => {},
    }
  )
}
